import React, { useRef, useState } from "react";
import { Button, Flex, Form, Grid, message, Spin, theme } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import Cookies from "js-cookie";
import { logInOtp } from "../Service/loginOtp";
import { usePermissions } from "../../context/PermissionContext";

const { useToken } = theme;
const { useBreakpoint } = Grid;

export default function LoginOtp() {
  const { email } = useParams();
  const navigate = useNavigate();
  const { token } = useToken();
  const screens = useBreakpoint();
  const { reload } = usePermissions(); // ← get the reload function from context

  const [isLoading, setIsLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [otp, setOtp] = useState(Array(6).fill(""));
  const inputRefs = useRef([]);

  const handleOtpChange = (index, value) => {
    if (!/^[0-9]?$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1]?.focus();
  };

  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").trim();
    if (!/^\d{6}$/.test(pasted)) return;
    const pastedOtp = pasted.split("");
    setOtp(pastedOtp);
    pastedOtp.forEach((digit, i) => {
      if (inputRefs.current[i]) inputRefs.current[i].value = digit;
    });
    inputRefs.current[5]?.focus();
    e.preventDefault();
  };

  const onFinish = () => {
    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      messageApi.open({
        type: "error",
        content: "Please enter the full 6-digit OTP!",
      });
      return;
    }

    setIsLoading(true);
    logInOtp({ otp: otpValue, email, type: "VERIFICATION" })
      .then(async (res) => {
        // 1. Write cookie first
        const AUTH_TOKEN_EXPIRY = new Date(Date.now() + 10 * 60 * 1000); // 10 min — matches backend
        const REFRESH_TOKEN_EXPIRY = new Date(Date.now() + 30 * 60 * 1000); // 30 min — matches backend

        Cookies.set("AUTH_TOKEN", JSON.stringify(res.data.accessToken), {
          expires: AUTH_TOKEN_EXPIRY,
        });
        Cookies.set("REFRESH_TOKEN", JSON.stringify(res.data.refreshToken), {
          expires: REFRESH_TOKEN_EXPIRY,
        });
        Cookies.set("user_details", JSON.stringify(res.data.userDetails), {
          expires: REFRESH_TOKEN_EXPIRY,
        }); // tie to refresh

        // 2. Load permissions NOW — before navigating — so the sidebar
        //    renders correctly on the very first page the user lands on.
        //    reload() reads the cookie we just set and fetches from the API.
        await reload();

        // 3. Navigate — permissions are already in context
        navigate("/", { replace: true });
      })
      .catch((err) => {
        messageApi.open({
          type: "error",
          content: err.response?.data?.message || err.message,
        });
      })
      .finally(() => setIsLoading(false));
  };

  const styles = {
    section: {
      minHeight: "100vh",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "flex-end",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      padding: screens.md ? `${token.sizeXXL}px 60px` : "10px",
    },
    container: {
      background: "#ffffff",
      borderRadius: "12px",
      boxShadow: "0 10px 40px rgba(0,0,0,0.1)",
      padding: "40px",
      width: "420px",
    },
    header: { textAlign: "center", marginBottom: "24px" },
    subtitle: { fontSize: "14px", color: "#718096" },
    otpContainer: {
      display: "flex",
      justifyContent: "center",
      gap: "10px",
      margin: "20px 0",
    },
    otpBox: {
      width: "48px",
      height: "52px",
      textAlign: "center",
      fontSize: "20px",
      borderRadius: "8px",
      border: "1px solid #d9d9d9",
      outline: "none",
      transition: "all 0.2s",
    },
    button: {
      height: "48px",
      fontSize: "16px",
      fontWeight: 600,
      borderRadius: "8px",
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      border: "none",
    },
    link: { color: "#667eea", fontSize: "14px", fontWeight: 500 },
  };

  const userDetails = Cookies.get("user_details");

  return userDetails ? (
    <Navigate to="/" replace />
  ) : (
    <>
      {contextHolder}
      <section
        style={{
          ...styles.section,
          backgroundImage: `url(${process.env.PUBLIC_URL}/images/home_page_image.png)`,
        }}
      >
        <div style={styles.container}>
          <div style={styles.header}>
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <img
                src={`${process.env.PUBLIC_URL}/images/step_to_innovation_logo.jpeg`}
                alt="Logo"
                style={{
                  width: "5rem",
                  borderRadius: "50%",
                  marginRight: "10px",
                }}
              />
              <h2>Step to Innovation</h2>
            </div>
            <p style={styles.subtitle}>
              Enter the 6-digit OTP sent to your email
            </p>
          </div>

          <Form onFinish={onFinish} layout="vertical">
            <Form.Item>
              <div style={styles.otpContainer} onPaste={handlePaste}>
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength="1"
                    value={digit}
                    style={styles.otpBox}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onFocus={(e) => e.target.select()}
                  />
                ))}
              </div>
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                block
                type="primary"
                htmlType="submit"
                style={styles.button}
                disabled={isLoading}
                icon={
                  isLoading ? (
                    <Spin
                      indicator={<SyncOutlined spin />}
                      style={{ color: "white" }}
                    />
                  ) : null
                }
              >
                {!isLoading && "Verify OTP"}
              </Button>
            </Form.Item>

            <Flex justify="space-between" style={{ marginTop: "16px" }}>
              <Link to="/login" style={styles.link}>
                Back to Login
              </Link>
              <Link to="/forget-password" style={styles.link}>
                Forgot password?
              </Link>
            </Flex>
          </Form>
        </div>
      </section>
    </>
  );
}
